// ============================================================================
// PAGE - courseCircuits.jsx
// ============================================================================
// client/src/pages/iot/courseCircuits.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackButton from '../../components/customs/backButton';
import PrevButton from '../../components/customs/prevButton';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import ProfIXChatbot from '../../components/chatBot/ProfIXChatbot';
import CustomButton from '../../components/customs/customButton';
import CustomAlert from '../../components/customs/customAlert';
import { CourseDomains, isHardwareDomain } from '../../types/courseDomains';

import { 
    formatDate, 
    formatShortDate, 
    formatLongDate, 
    formatRelativeTime,
    formatDateTime 
} from '../../utilities/dateUtils';

import { GlobalContext } from '../../context/provider';
import useVisualCircuit from '../../hooks/useVisualCircuit';
import useCourse from '../../hooks/useCourse';

import '../../styles/globalstyles.css';
import './courseCanvases.css';

const CourseCanvases = ({ isModal = false, onClose = null, course_Id }) => {
    const courseId = useParams().courseId ?? course_Id;
    const navigate = useNavigate(); 
    const [activeTab, setActiveTab] = useState('all');
    const [selectedCircuit, setSelectedCircuit] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [labSelectionModalVisible, setLabSelectionModalVisible] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [sortAscending, setSortAscending] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [selectedLabFilter, setSelectedLabFilter] = useState('all'); // 'all', 'practice', or labId
    const [selectedPersonFilter, setSelectedPersonFilter] = useState('all'); // ← add this
    const [isLoaded, setIsLoaded] = useState(false);
    const [domainName, setDomainName] = useState('');

    // Alert states
    const [alertVisible, setAlertVisible] = useState(false);
    const [circuitToDelete, setCircuitToDelete] = useState(null);
    const [courseInfo, setCourseInfo] = useState(null);

    // Hooks
    const { 
        courseCircuits,
        enrolledUsers,
        currentCircuit,
        isLoading,
        error,
        getCircuitsByCourse,
        getCircuitById,
        createCircuit,
        deleteCircuit,
        cloneCircuit,
        clearError
    } = useVisualCircuit();

    const { getCourseById } = useCourse();

    // Global context
    const { 
        authState: { authData },
    } = useContext(GlobalContext);
    
    // Load circuits and course on mount
    useEffect(() => {
        const loadData = async () => {
        try {
            // ✅ ADD GUARD: Only load if we have authData
            if (courseId && authData?.id) {
            
            const isElevated = authData.role === 'admin' || authData.role === 'instructor';
            await getCircuitsByCourse(
                courseId,
                isElevated ? undefined : authData.id,
                isElevated ? authData.role : undefined,
                isElevated ? authData.id : undefined
            );

            const course = await getCourseById(courseId);
            setCourseInfo(course.data);
            setDomainName(course.data.category);
            setIsLoaded(true);
            }
        } catch (err) {
            console.error('Error loading circuits:', err);
            setIsLoaded(true);
        }
        };
        
        loadData();
    }, [courseId, authData?.id]); // ✅ ADD authData?.id to dependencies
    
    // Filter circuits by selected lab
    const getFilteredByLab = () => {
        if (selectedLabFilter === 'all') {
        return courseCircuits;
        } else if (selectedLabFilter === 'practice') {
        // Circuits without labId
        return courseCircuits.filter(c => !c.labId);
        } else {
        // Circuits for specific lab
        return courseCircuits.filter(c => c.labId === selectedLabFilter);
        }
    };
    
    const getFilteredByPerson = () => {
        const labFiltered = getFilteredByLab();
        if (selectedPersonFilter === 'all') return labFiltered;
        return labFiltered.filter(c => c.studentId === selectedPersonFilter);
    };
    const displayCircuits = getFilteredByPerson();
    
    const filteredCircuits = displayCircuits.filter(circuit => {
        const matchesSearch = !searchQuery || 
        circuit.name.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesStatus = !filterStatus || circuit.status === filterStatus;
        
        const matchesTab = activeTab === 'all' || 
        (activeTab === 'validated' && circuit.isValid) ||
        (activeTab === 'draft' && circuit.status === 'draft');
        
        return matchesSearch && matchesStatus && matchesTab;
    });

    // Sort circuits
    const sortedCircuits = [...filteredCircuits].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return sortAscending ? dateA - dateB : dateB - dateA;
    });

    // Count circuits by status
    const validatedCount = displayCircuits.filter(c => c.isValid).length;
    const draftCount = displayCircuits.filter(c => c.status === 'draft').length;

    // Handlers
    const handleOpenLabSelectionModal = () => {
        setLabSelectionModalVisible(true);
    };

    const handleCreateCircuitForLab = async (labId) => {
        try {
        const selectedLab = courseInfo?.labs?.find(lab => lab.labId === labId);
        
        // const newCircuit = {
        //     studentId: authData.id,
        //     courseId: courseId,
        //     labId: labId,
        //     name: `${labId} - ${domainName} Circuit ${displayCircuits.filter(c => c.labId === labId).length + 1}`,
        //     description: `Circuit for ${selectedLab?.title || 'Lab'}`,
        //     boardType: 'ESP32',
        //     boardPosition: { x: 400, y: 300 },
        //     components: [],
        //     connections: [],
        //     canvasSettings: {
        //     zoom: 1,
        //     panX: 0,
        //     panY: 0,
        //     gridSize: 20,
        //     snapToGrid: true,
        //     showGrid: true,
        //     backgroundColor: '#ffffff'
        //     },
        //     status: 'draft'
        // };

        const isHardwareDomain = isHardwareDomain(domainName);
        const newCircuit = {
            studentId:  authData.id,
            courseId:   courseId,
            labId:      labId,
            domainName: domainName,
            name:       `${labId} - ${domainName} Circuit ${displayCircuits.filter(c => c.labId === labId).length + 1}`,
            description: `Circuit for ${selectedLab?.title || 'Lab'}`,
            ...(isHardwareDomain && {
                boardType:     'ESP32',
                boardPosition: { x: 400, y: 300 },
            }),
            components:     [],
            connections:    [],
            canvasSettings: {
                zoom:            1,
                panX:            0,
                panY:            0,
                gridSize:        20,
                snapToGrid:      true,
                showGrid:        true,
                backgroundColor: '#ffffff',
            },
            status: 'draft',
        };


        const response = await createCircuit(newCircuit);

        if (response.success) {
            setLabSelectionModalVisible(false);
            const url = `/labengine/course/${courseId}/lab/${labId}/domain/${domainName}/circuit/buildcanvas?circuitId=${response.data.circuitId}`;
            window.open(url, '_blank', 'noopener,noreferrer');
            // navigate(url);
        }
        } catch (err) {
        console.error('Error creating circuit:', err);
        }
    };

    const handleCreatePracticeCircuit = async () => {
        try {
        const practiceCount = courseCircuits.filter(c => !c.labId).length;
        
        // const newCircuit = {
        //     studentId: authData.id,
        //     courseId: courseId,
        //     // No labId - practice circuit
        //     name: `${domainName} Practice ${practiceCount + 1}`,
        //     description: `${domainName} practice circuit - not associated with any lab`,
        //     boardType: 'ESP32',
        //     boardPosition: { x: 400, y: 300 },
        //     components: [],
        //     connections: [],
        //     canvasSettings: {
        //     zoom: 1,
        //     panX: 0,
        //     panY: 0,
        //     gridSize: 20,
        //     snapToGrid: true,
        //     showGrid: true,
        //     backgroundColor: '#ffffff'
        //     },
        //     status: 'draft'
        // };

        const isHardwareDomain = isHardwareDomain(domainName);
        const newCircuit = {
            studentId:  authData.id,
            courseId:   courseId,
            domainName: domainName,
            name:       `${domainName} Practice ${practiceCount + 1}`,
            description: `${domainName} practice circuit - not associated with any lab`,
            ...(isHardwareDomain && {
                boardType:     'ESP32',
                boardPosition: { x: 400, y: 300 },
            }),
            components:     [],
            connections:    [],
            canvasSettings: {
                zoom:            1,
                panX:            0,
                panY:            0,
                gridSize:        20,
                snapToGrid:      true,
                showGrid:        true,
                backgroundColor: '#ffffff',
            },
            status: 'draft',
        };

        const response = await createCircuit(newCircuit);
        
        if (response.success) {
            setLabSelectionModalVisible(false);
            // Navigate to practice circuit builder (no labId in URL)
            const url = `/labengine/course/${courseId}/domain/${domainName}/buildcanvas?circuitId=${response.data.circuitId}`;
            window.open(url, '_blank', 'noopener,noreferrer');
            // navigate(url);
        }
        } catch (err) {
        console.error('Error creating practice circuit:', err);
        }
    };

    const isElevatedUser = authData.role === 'admin' || authData.role === 'instructor';
    const isOwnCircuit = (circuit) => {
        console.log('circuit.studentId:', circuit?.studentId, 'authData.id:', authData.id, 'authData.loginId:', authData.loginId);
        return circuit.studentId === authData.id || circuit.studentId === authData.loginId;
    };

    const handleEditCircuit = (circuit) => {
        const ownerParam = !isOwnCircuit(circuit) ? `&ownerId=${circuit.studentId}` : '';
        const ownerParamFirst = !isOwnCircuit(circuit) ? `?ownerId=${circuit.studentId}` : '';

        if (circuit.sessionId) {
            if (circuit.labId) {
                const url = `/labengine/course/${courseId}/lab/${circuit.labId}/domain/${domainName}/buildcanvas?sessionId=${circuit.sessionId}${ownerParam}`;
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                const url = `/labengine/course/${courseId}/domain/${domainName}/buildcanvas?sessionId=${circuit.sessionId}${ownerParam}`;
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        } else {
            if (circuit.labId) {
                const url = `/labengine/course/${courseId}/lab/${circuit.labId}/domain/${domainName}/buildcanvas?circuitId=${circuit.circuitId}${ownerParam}`;
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                const url = `/labengine/course/${courseId}/domain/${domainName}/buildcanvas?circuitId=${circuit.circuitId}${ownerParam}`;
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        }
    };

    const handleDuplicateCircuit = async (circuit) => {
        try {
        const response = await cloneCircuit(circuit._id, authData.id);
        
        if (response.success) {
            await getCircuitsByCourse(
                courseId,
                isElevatedUser ? undefined : authData.id,
                isElevatedUser ? authData.role : undefined,
                isElevatedUser ? authData.id : undefined
            );
        }
        } catch (err) {
        console.error('Error duplicating circuit:', err);
        }
    };

    const handleDeleteCircuit = (circuit) => {
        setCircuitToDelete(circuit);
        setAlertVisible(true);
    };

    const confirmDelete = async () => {
        try {
        if (circuitToDelete) {
            await deleteCircuit(circuitToDelete._id);
            await getCircuitsByCourse(
                courseId,
                isElevatedUser ? undefined : authData.id,
                isElevatedUser ? authData.role : undefined,
                isElevatedUser ? authData.id : undefined
            );
            setAlertVisible(false);
            setCircuitToDelete(null);
        }
        } catch (err) {
        console.error('Error deleting circuit:', err);
        }
    };

    const handleViewDetails = async (circuit) => {
        try {
        await getCircuitById(circuit._id);
        setSelectedCircuit(circuit);
        setModalVisible(true);
        } catch (err) {
        console.error('Error loading circuit details:', err);
        }
    };

    const handleViewSimulator = (circuit) => {
        if (!circuit?.sessionId) {
            alert('⚠️ Please sync to simulator first!');
            return;
        }      
        const simulatorRoute = CourseDomains[domainName]?.simulator ?? 'simulator';
        navigate(`/labengine/course/${courseId}/${simulatorRoute}?sessionId=${circuit.sessionId}`);  
    };

    const getStatusBadge = (circuit) => {
        if (circuit.isValid) {
        return <div className="circuit-badge badge-valid">✅ VALID</div>;
        } else if (circuit.validationErrors?.length > 0) {
        const errorCount = circuit.validationErrors.filter(e => e.type === 'error').length;
        if (errorCount > 0) {
            return <div className="circuit-badge badge-error">❌ ERRORS</div>;
        } else {
            return <div className="circuit-badge badge-warning">⚠️ WARNINGS</div>;
        }
        } else {
        return <div className="circuit-badge badge-draft">📝 DRAFT</div>;
        }
    };

    const getStatusIcon = (status) => {
        switch(status) {
        case 'validated': return '✅';
        case 'deployed': return '🚀';
        case 'archived': return '📦';
        default: return '📝';
        }
    };

    const getLabName = (labId) => {
        const lab = courseInfo?.labs?.find(l => l.labId === labId);
        return lab ? lab.title : 'Unknown Lab';
    };

    const getOwnerName = (studentId) => {
        // Check if it's the logged-in user themselves
        if (studentId === authData.id) return authData.name;
        // Check enrolled students list
        const user = enrolledUsers.find(u => u.loginId === studentId);
        return user ? user.name : studentId;
    };

    const profileHeaderData = { 
        ...authData, 
        // title: '🔧 My Circuit Designs',
        // subTitle: `${courseInfo?.courseId || 'Course'} - All Labs`
    };

    return (
        <div className="page-container">
            {/* Header Section - Hide in modal mode */}
            <div className="page-header">
                <ProfileHeader 
                data={profileHeaderData}
                activePage="circuits" 
                />
                {/* Close button for modal mode */}
                <div className="headerMiddle">
                    <ProfIXChatbot 
                        userName={authData.name}
                        courseId={courseId} 
                        courseName={courseInfo?.title} 
                        category={courseInfo?.category}
                    />
                </div>
                <SharedHeader courseId={courseId} domainId={domainName} icons={['addCanvas']} />
            </div>

            {/* Toolbar */}
            <div className="page-toolbar">
                <BackButton 
                    to="-1"
                    activeTab="management"
                    label="←"
                    bgColor="#6b7280"
                />
                <span className="page-toolbar-title-wrap">
                    <span className="page-toolbar-title">🔧 Practical Labs Workflow</span>
                    <span className="page-toolbar-subtitle">📚 Course: {courseInfo?.title} ({courseInfo?.courseId})</span>
                </span>
            </div>

            {/* Lab Filter Section */}
            <div className="page-toolbar">
                <div className="double_search">
                    <label htmlFor="lab-filter" style={{color: 'white'}}>📋 Filter by Lab:</label>
                    <select
                        id="lab-filter"
                        className="dropdown circuit-lab-filter-dropdown"
                        style={{color: 'white'}}
                        value={selectedLabFilter}
                        onChange={(e) => setSelectedLabFilter(e.target.value)}
                    >
                        <option value="all">All Circuits ({courseCircuits.length})</option>
                        <option value="practice">Practice Only ({courseCircuits.filter(c => !c.labId).length})</option>
                        {courseInfo?.labs?.map(lab => (
                            <option key={lab.labId} value={lab.labId}>
                                {lab.title} ({courseCircuits.filter(c => c.labId === lab.labId).length})
                            </option>
                        ))}
                    </select>
                </div>

                {/* Person filter — admin and instructor only */}
                {isElevatedUser && (
                    <div className="double_search">
                        <label htmlFor="person-filter" style={{color: 'white'}}>👤 Filter by Person:</label>
                        <select
                            id="person-filter"
                            className="dropdown circuit-lab-filter-dropdown"
                            value={selectedPersonFilter}
                            onChange={(e) => setSelectedPersonFilter(e.target.value)}
                        >
                            <option value="all">
                                All People ({courseCircuits.length})
                            </option>
                            <option value={authData.id}>
                                Me ({courseCircuits.filter(c => c.studentId === authData.id).length})
                            </option>
                            {enrolledUsers
                                .filter(u => u.loginId !== authData.id)
                                .map(user => (
                                    <option key={user.loginId} value={user.loginId}>
                                        {user.name} ({courseCircuits.filter(c => c.studentId === user.loginId).length})
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                )}

                <div className="double_search">
                    <label htmlFor="lab-filter" style={{color: 'white'}}>📋 Filter by Status:</label>
                    <select
                        className="dropdown circuit-lab-filter-dropdown"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="">All Circuits ({courseCircuits.length})</option>
                        <option value="draft">Draft</option>
                        <option value="validated">Validated</option>
                        <option value="deployed">Deployed</option>
                        <option value="archived">Archived</option>
                    </select>
                </div>
            </div>

            {/* Circuits List */}
            <div className="page-content">
                {!isLoaded ? (
                <div className="loading-container">
                    <p>Loading circuits...</p>
                </div>
                ) : isLoading ? (
                <div className="loader" />
                ) : sortedCircuits.length > 0 ? (
                <div className="courses-grid">
                    {sortedCircuits.map((circuit) => (
                    <div
                        key={circuit._id}
                        className={`circuit-card ${circuit.isValid ? 'circuit-card--valid' : ''}`}
                        onClick={() => handleViewDetails(circuit)}
                    >
                        <div className="badge-container">
                        {getStatusBadge(circuit)}
                        {!circuit.labId && (
                            <div className="circuit-badge badge-practice">🎯 PRACTICE</div>
                        )}
                        </div>

                        <div className="circuit-card-meta">
                            <p className="circuit-card-title">{circuit.name}</p>
                            <p>
                                {getStatusIcon(circuit.status)} {circuit.components?.length || 0} Components
                            </p>
                            <p>
                                {circuit.labId ? (
                                `📚 Lab: ${getLabName(circuit.labId)}`
                                ) : (
                                '🎯 Practice Circuit'
                                )}
                            </p>
                            <p className={circuit.sessionId ? 'circuit-synced' : 'circuit-unsynced'}>
                                {circuit.sessionId ? (
                                `✅ Synced to simulator`
                                ) : (
                                '⚠️ Not synced to simulator'
                                )}
                            </p>
                            <p>
                                📅 Created at: {formatShortDate(circuit.createdAt)}
                            </p>
                            {isElevatedUser && (
                                <p className="circuit-owner-tag">
                                    👤 {getOwnerName(circuit.studentId)}
                                    {circuit.studentId === authData.id && ' (Me)'}
                                </p>
                            )}
                        </div>

                    </div>
                    ))}
                </div>
                ) : (
                <div className="no-items">
                    <p>No circuits available 🔧</p>
                    <p className="circuit-empty-hint">
                    Create your first circuit or use a template to get started!
                    </p>
                </div>
                )}
            </div>

            {/* Lab Selection Modal */}
            {labSelectionModalVisible && (
            <div className="modal-overlay">
                <div className="modal-content">
                <div className="modal-header">
                    <h3>🔧 Create New Circuit</h3>
                    <button
                    className="modal-close-btn"
                    onClick={() => setLabSelectionModalVisible(false)}
                    >
                    ✖
                    </button>
                </div>

                <div className="modal-body">
                    <p className="circuit-modal-hint">
                    Select a lab for your circuit, or create a practice circuit:
                    </p>

                    {/* Practice Option */}
                    <div 
                    className="circuit-lab-selection-item circuit-practice-option"
                    onClick={handleCreatePracticeCircuit}
                    >
                    <div className="circuit-lab-icon">🎯</div>
                    <div className="circuit-lab-info">
                        <h4>Practice Circuit</h4>
                        <p>Not associated with any lab - for practice only</p>
                    </div>
                    </div>

                    {/* Lab Options */}
                    <div className="circuit-lab-selection-list">
                    {courseInfo?.labs?.map(lab => (
                        <div 
                        key={lab.labId}
                        className="circuit-lab-selection-item"
                        onClick={() => handleCreateCircuitForLab(lab.labId)}
                        >
                        <div className="circuit-lab-icon">📚</div>
                        <div className="circuit-lab-info">
                            <h4>{lab.title}</h4>
                            <p>{lab.description || 'No description'}</p>
                        </div>
                        </div>
                    ))}
                    </div>
                </div>
                </div>
            </div>
            )}

            {/* Detail Modal */}
            {modalVisible && selectedCircuit && (
            <div className="modal-overlay">
                <div className="modal-content-top">
                <div className="modal-header-top">
                    <div className="modal-info-group">
                    <div className="modal-header-title">
                        <h3>{selectedCircuit.name}</h3>
                    </div>
                    </div>

                    <div className="modal-actions-group">
                    <button
                        className="modal-close-btn"
                        onClick={() => {
                        setModalVisible(false);
                        setSelectedCircuit(null);
                        }}
                    >
                        ✖
                    </button>
                    </div>
                </div>

                <div className="modal-body">
                    {/* Stats Section */}
                    <div className="cc_info_stats">
                        <div className="cc_info_item">
                            <span className="cc_info_icon">🔧</span>
                            <div>
                                <div className="cc_info_label">Components</div>
                                <div className="cc_info_value">{selectedCircuit.components?.length || 0}</div>
                            </div>
                        </div>
                        <div className="cc_info_item">
                            <span className="cc_info_icon">🔌</span>
                            <div>
                                <div className="cc_info_label">Connections</div>
                                <div className="cc_info_value">{selectedCircuit.connections?.length || 0}</div>
                            </div>
                        </div>
                        <div className="cc_info_item">
                            <span className="cc_info_icon">{selectedCircuit.isValid ? '✅' : '❌'}</span>
                            <div>
                                <div className="cc_info_label">Validation</div>
                                <div className="cc_info_value">{selectedCircuit.isValid ? 'Valid' : 'Invalid'}</div>
                            </div>
                        </div>
                        <div className="cc_info_item">
                            <span className="cc_info_icon">{selectedCircuit.isSynced ? '✅' : '❌'}</span>
                            <div>
                                <div className="cc_info_label">Simulator</div>
                                <div className="cc_info_value">{selectedCircuit.isSynced ? 'Synced' : 'Not synced'}</div>
                            </div>
                        </div>
                        {isElevatedUser && (
                            <div className="cc_info_item">
                                <span className="cc_info_icon">👤</span>
                                <div>
                                    <div className="cc_info_label">Owner</div>
                                    <div className="cc_info_value">
                                        {getOwnerName(selectedCircuit.studentId)}
                                        {isOwnCircuit(selectedCircuit) && ' (Me)'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Circuit Details Action Buttons */}
                    <div 
                        className="page-title-bar" 
                        onClick={() => setIsExpanded(prev => !prev)}
                    >
                        <h4>📋 Circuit Details</h4>
                        <div style={{display: 'flex', justifyContent: 'space-evenly'}}>

                        {(isOwnCircuit(selectedCircuit) || isElevatedUser) && (
                            <button className="circuit-info-btn" onClick={() => handleEditCircuit(selectedCircuit)} title="Edit Canvas">
                                <span>✏️ </span><span>Edit Canvas</span>
                            </button>
                        )}

                        {selectedCircuit.sessionId && (
                            <button className="circuit-info-btn" onClick={() => handleViewSimulator(selectedCircuit)}>
                                <span>{CourseDomains[domainName]?.simulatorIcon || '🖥️ '}</span>
                                <span>{CourseDomains[domainName]?.simulatorButtonLabel || 'Open Simulator'}</span>
                            </button>
                        )}

                        {(isOwnCircuit(selectedCircuit) || isElevatedUser) && (
                            <button className="circuit-info-btn" onClick={() => { handleDuplicateCircuit(selectedCircuit); setModalVisible(false); }} title="Duplicate Canvas">
                                <span>📋 </span><span>Duplicate Canvas</span>
                            </button>
                        )}

                        {(isOwnCircuit(selectedCircuit) || isElevatedUser) && (
                            <button 
                                className="circuit-delete-btn" 

                                onClick={() => { setModalVisible(false); handleDeleteCircuit(selectedCircuit); }} 
                                title="Delete Canvas">
                                <span>🗑️ </span><span>Delete Canvas</span>
                            </button>
                        )}

                        </div>
                    </div>

                    {/* Detail Section */}
                    <div className="session-info">
                        {currentCircuit.sessionId ? (
                            <div className="summary-item">
                                <strong>🔗 Session ID:</strong> {currentCircuit.sessionId || 'Not linked'}
                            </div>
                        ):(
                           <div className="summary-item">
                                <strong>🔖 Circuit ID:</strong> {currentCircuit.circuitId}
                            </div> 
                        )}
                        {currentCircuit.boardType && (
                            <div className="summary-item">
                                <strong>📂 Board Type:</strong> {currentCircuit.boardType}
                            </div>
                        )}
                        <div className="summary-item">
                            <strong>✨ Status:</strong> {currentCircuit.status}
                        </div>
                        <div className="summary-item">
                            <strong> {currentCircuit.labId ? '📚 Lab:' : '📚 Practice'}</strong> 
                            {currentCircuit.name}
                        </div>
                        {/* <div className="summary-item">
                        <strong>🔗 Simulator ID:</strong> {currentCircuit.sessionId || 'Not linked'}
                        </div> */}
                        <div className="summary-item">
                            <strong>📝 Description:</strong> {currentCircuit.description || 'No description'}
                        </div>
                        <div className="summary-item">
                            <strong>📅 Created:</strong> {new Date(currentCircuit.createdAt).toLocaleString()}
                        </div>
                        {currentCircuit.lastValidated && (
                        <div className="summary-item">
                            <strong>✅ Last Validated:</strong> {new Date(currentCircuit.lastValidated).toLocaleString()}
                        </div>
                        )}
                    </div>

                    {/* Validation Errors Section */}
                    {selectedCircuit.validationErrors && selectedCircuit.validationErrors.length > 0 && (
                    <div className="conversation-container">
                        <div className="page-title-bar">
                        <h4>⚠️ Validation Issues</h4>
                        </div>

                        <div className="message-scroll">
                        {selectedCircuit.validationErrors.map((error, idx) => (
                            <div 
                            key={idx} 
                            className={`circuit-validation-error ${error.type === 'error' ? 'validation-error-critical' : 'validation-error-warning'}`}
                            >
                            <strong>{error.type === 'error' ? '❌' : '⚠️'} {error.type.toUpperCase()}:</strong> {error.message}
                            {error.component && <div className="validation-error-detail">Component: {error.component}</div>}
                            {error.connection && <div className="validation-error-detail">Connection: {error.connection}</div>}
                            </div>
                        ))}
                        </div>
                    </div>
                    )}

                    {/* Components List */}
                    <div className="conversation-container">
                    <div className="page-title-bar">
                        <h4>🔧 Components ({currentCircuit?.components?.length || 0})</h4>
                        <button onClick={() => setSortAscending(prev => !prev)} className="sort-button">
                        {sortAscending ? '📅 A-Z' : '📅 Z-A'}
                        </button>
                    </div>

                    <div className="message-scroll">
                        {currentCircuit?.components && currentCircuit.components.length > 0 ? (
                        currentCircuit.components.map((component, idx) => (
                            <div key={idx} className="circuit-component-item">
                            <strong>📦 {component.componentName}</strong>
                            <div className="circuit-component-details">
                                Category: {component.category} | Instance: {component.instanceId}
                            </div>
                            {component.label && (
                                <div className="circuit-component-details">
                                Label: {component.label}
                                </div>
                            )}
                            {component.linkedSensorId && (
                                <div className="circuit-component-sensor-link">
                                🔗 Linked Sensor: {component.linkedSensorId}
                                </div>
                            )}
                            </div>
                        ))
                        ) : (
                        <div className="no-messages">
                            <p>No components added yet</p>
                        </div>
                        )}
                    </div>
                    </div>
                </div>
                </div>
            </div>
            )}

            {/* Delete Confirmation Alert */}
            <CustomAlert
                visible={alertVisible}
                title="Delete Canvas"
                icon="🗑️"
                message={`Are you sure you want to delete "${circuitToDelete?.name}"? This action cannot be undone.`}
                onConfirm={confirmDelete}
                onCancel={() => {
                    setAlertVisible(false);
                    setCircuitToDelete(null);
                }}
            />

            {/* Error Display */}
            {error && (
            <div className="circuit-error-container">
                ❌ Error: {error}
                <button 
                onClick={clearError}
                className="circuit-error-close"
                >
                ✖
                </button>
            </div>
            )}

        </div>
    );
};

export default CourseCanvases;